import React, { useState } from "react";
import {
  Button,
  Grid,
  Box,
  Checkbox, 
  FormControlLabel,
  TextField 
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { updateAgreementStatus, exportAgreements } from "../../services/agreementServices";
import useAgreementsData from "../../hooks/useAgreementsData";
import AgreementTable from "./AgreementTable";
import AgreementFilters from "./AgreementFilter";
import { STATUS_OPTIONS_PRIVATE } from "../../constants/status";
import { handleApiError } from "../../utils/errorHandler";
import { generatePublicAgreementsToPdf } from "../../pdfs/generatePublicAgreementsToPdf";

export default function AgreementList() {
  const navigate = useNavigate();
  const location = useLocation();

  // Leer parámetros desde URL
  const searchParams = new URLSearchParams(location.search);
  const currentPage = Number(searchParams.get("page") || 1);
  const searchQuery = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || "all";
  const typeFilter = searchParams.get("type") || "";
  const subscriptionFrom = searchParams.get("subscription_from") || "";
  const subscriptionTo = searchParams.get("subscription_to") || "";
  const validityFrom = searchParams.get("validity_from") || "";
  const validityTo = searchParams.get("validity_to") || "";

  const [useAI, setUseAI] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [loadingPDF, setLoadingPDF] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const {
    agreements,
    loading,
    error,
    totalAgreements,
  } = useAgreementsData(
    searchQuery,
    currentPage,
    statusFilter,
    { typeFilter, subscriptionFrom, subscriptionTo, validityFrom, validityTo },
    refreshKey
  );

  const updateURL = (updates = {}) => {
    const params = new URLSearchParams(location.search);
  
    // Aplicar actualizaciones
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
  
    navigate(`/agreements?${params.toString()}`);
  };
  

  const handleAddAgreement = () => {
    navigate(`/agreement/new?${location.search}`);
  };

  const handleShowAgreement = (id) => {
    navigate(`/agreement/show/${id}?${location.search}`, {
      state: {
        id,
        displayHead: true,
        mainTitle: "Información del convenio",
      },
    });
  };

  const handleReport = (id, agreement_type) => {
    
    navigate(`/agreement/analyze/${id}?${location.search}`, {
      state: { id, generateReport: true, agreement_type},
    });
  };

  const handleEditAgreement = (id) => {
    navigate(`/agreement/edit?id=${id}&${location.search}`, { state: { id } });
  };

  const handleEditAgreementIndicator = (id) => {
    navigate(`/agreement/edit/indicator?id=${id}&${location.search}`);
  };

  const onHandleUpdateStatus = async (id, newStatus) => {
    try {
      await updateAgreementStatus(id, newStatus);
      toast.success("Se actualizó el estado del convenio");
      setRefreshKey((prev) => prev + 1); // Forzar recarga
    } catch (err) {
      handleApiError(err, "Error al actualizar estado");
    }
  };

  // Acciones para filtros
  const handleFilterChange = (filterName, value) => {
    updateURL({
      [filterName]: value,
      page: 1, // Reiniciar a primera página al cambiar filtros
    });
  };
  

  // Paginación
  const handlePageChange = (event, value) => {
    updateURL({ page: value });
  };
  

  // Limpiar filtros
  const handleClearFilters = () => {
    navigate(`/agreements?page=1`);
  };

  const handleExportInternal = async () => {
    try {
      setLoadingPDF(true);
      if (useAI) {
        toast.info("Generando informe IA...");
      } else {
        toast.info("Generando informe...");
      }
      const filters = {
        q: searchQuery,
        status: statusFilter,
        type: typeFilter,
        subscription_from: subscriptionFrom,
        subscription_to: subscriptionTo,
        validity_from: validityFrom,
        validity_to: validityTo,
      };

      const response = await exportAgreements(filters, "internal");
      const allAgreements = response.data.data.agreements;


      await generatePublicAgreementsToPdf(allAgreements, filters, useAI, customPrompt);
      toast.success("Informe generado con éxito");
    } catch (err) {
      console.error("Error al exportar PDF interno:", err);
      toast.error("Error al generar el PDF");
    } finally {
      setLoadingPDF(false); 
    }
  };

  
  

  return (
    <Box sx={{ p: 5 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <h2>Lista de convenios</h2>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleAddAgreement}>
            Agregar
          </Button>
        </Grid>
      </Grid>

      <AgreementFilters
        searchQuery={searchQuery}
        onSearchChange={(val) => handleFilterChange("q", val)}
        statusFilter={statusFilter}
        onStatusChange={(val) => handleFilterChange("status", val)}
        subscriptionFrom={subscriptionFrom}
        onSubscriptionFromChange={(val) =>
          handleFilterChange("subscription_from", val)
        }
        subscriptionTo={subscriptionTo}
        onSubscriptionToChange={(val) =>
          handleFilterChange("subscription_to", val)
        }
        validityFrom={validityFrom}
        onValidityFromChange={(val) => handleFilterChange("validity_from", val)}
        validityTo={validityTo}
        onValidityToChange={(val) => handleFilterChange("validity_to", val)}
        typeFilter={typeFilter}
        onTypeChange={(val) => handleFilterChange("type", val)}
        onClearFilters={handleClearFilters}
        statusOptions={STATUS_OPTIONS_PRIVATE}
      />
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
        <Button
          onClick={handleExportInternal}
          variant="contained"
          size="medium"
          disabled={loadingPDF}
          sx={{ width: "auto", px: 3 }} 
        >
          {loadingPDF ? "Generando PDF..." : "Exportar a PDF"}
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={useAI}
              onChange={(e) => setUseAI(e.target.checked)}
              disabled={loadingPDF}
            />
          }
          label="Incluir informe generado por IA"
        />
      </Box>


      {useAI && (
        <TextField
          label="Prompt personalizado"
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 2 }}
          disabled={loadingPDF}
        />
      )}

      <AgreementTable
        agreements={agreements}
        page={currentPage}
        totalCount={totalAgreements}
        onPageChange={handlePageChange}
        showActions={true}
        onShow={handleShowAgreement}
        onEdit={handleEditAgreement}
        onEditIndicators={handleEditAgreementIndicator}
        onReport={handleReport}
        onUpdateStatus={onHandleUpdateStatus}
      />
    </Box>
  );
}
